import { HandleModalProps } from "@/types/props";
import { memo, useEffect, useLayoutEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Controller } from "react-hook-form";
import { ZodError } from "@/components/ZodDisplayError";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { userSchema, UserSchemaType } from "@/components/validations/user";
import { emptyUser } from "@/models/empty";
import { Button } from "@/components/ui/button";
import { NgocInput } from "@/components/ui/ngocInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PERMISSIONS } from "@/lib";

export const UserModal = memo(({ isOpen, onClose, onAdd, onUpdate, editData, mode }: HandleModalProps) => {
    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        trigger,
        formState: { errors, isValid },
    } = useForm<UserSchemaType>({
        resolver: zodResolver(userSchema),
        defaultValues: emptyUser,
        mode: "all",
    });
    const [permissions, setPermissions] = useState<string[]>([]);

    const submit = (data: UserSchemaType) => {
        switch (mode) {
            case "create":
                onAdd(data as Omit<UserSchemaType, "id">);
                break;
            case "edit":
                setPermissions([...editData.permissions]);
                onUpdate(data);
                break;
        }
        onClose();
    };

    useLayoutEffect(() => {
        setPermissions([]);
    }, [isOpen]);

    useEffect(() => {
        switch (mode) {
            case "create":
                reset(emptyUser);
                break;
            case "edit": {
                reset({
                    id: editData.id,
                    name: editData.name,
                    email: editData.email,
                    currentEmail: editData.email,
                    username: editData.username || "",
                    role: editData.role,
                    status: editData.status,
                    department: editData.department || "",
                    position: editData.position || "",
                    phone: editData.phone || "",
                    permissions: [...editData.permissions],
                });
                trigger();
                setPermissions([...editData.permissions]);
                break;
            }
        }
    }, [isOpen, editData, mode, reset, trigger]);
    const handlePermissionChange = (permissionKey: string, checked: boolean) => {
        setPermissions((prev) => (checked ? [...prev, permissionKey] : prev.filter((p) => p !== permissionKey)));
        setValue("permissions", checked ? [...permissions, permissionKey] : permissions.filter((p) => p !== permissionKey));
    };
    return <Dialog open={isOpen} onOpenChange={() => { onClose() }}>
        <DialogContent className='max-w-4xl gap-0'>
            <DialogHeader>
                <DialogTitle>{mode === "edit" ? "Chỉnh Sửa Tài Khoản" : "Thêm Mới"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(submit)} className='pt-4 flex flex-col max-h-[75vh]'>
                <div className="space-y-2 h-full overflow-y-scroll">
                    <div className="space-y-2 px-1">
                        <Label className='text-base text-red-500'>Thông tin tài khoản</Label>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                            <div className='space-y-1'>
                                <Label htmlFor='name' className='required'>
                                    Họ và tên
                                </Label>
                                <NgocInput id='name' placeholder='Nhập họ tên' maxLength={255} {...register("name")} autoComplete="off" />
                                <ZodError message={errors.name?.message} />
                            </div>
                            <div className='space-y-1'>
                                <Label htmlFor='email' className='required'>
                                    Email
                                </Label>
                                <NgocInput id='email' placeholder='Nhập email' {...register("email")} maxLength={150} autoComplete="off" />
                                <ZodError message={errors.email?.message} />
                            </div>
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                            <div className='space-y-1'>
                                <Label htmlFor='username' className='required'>
                                    Tên đăng nhập
                                </Label>
                                <NgocInput id='username' placeholder='Nhập tên đăng nhập' maxLength={255} {...register("username")} />
                                <ZodError message={errors.username?.message} />
                            </div>
                            <div className='space-y-1'>
                                <Label htmlFor='role' className=''>
                                    Vai trò
                                </Label>
                                <Controller
                                    name='role'
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                            }}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder='Chọn vai trò' />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='admin'>Quản trị viên</SelectItem>
                                                <SelectItem value='user'>Người dùng</SelectItem>
                                                <SelectItem value='viewer'>Xem</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                            <div className='space-y-1'>
                                <Label htmlFor='department'>Phòng ban</Label>
                                <NgocInput id='department' placeholder='Nhập tên phòng ban' {...register("department")} />
                            </div>
                            <div className='space-y-1'>
                                <Label htmlFor='position'>Chức vụ</Label>
                                <NgocInput id='position' placeholder='Nhập tên chức vụ' {...register("position")} />
                            </div>
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                            <div className='space-y-1'>
                                <Label htmlFor='phone'>Số điện thoại</Label>
                                <NgocInput id='phone' placeholder='Nhập số điện thoại' {...register("phone")} />
                            </div>
                            <div className='space-y-1'>
                                <Label htmlFor='status'>Trạng thái</Label>
                                <Controller
                                    name='status'
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                            }}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='active'>Hoạt động</SelectItem>
                                                <SelectItem value='inactive'>Không hoạt động</SelectItem>
                                                <SelectItem value='suspended'>Bị đình chỉ</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='space-y-2'>
                        <Label className='text-base text-red-500'>Phân quyền truy cập</Label>
                        <div className="flex gap-1">
                            <Label htmlFor="select-all-permissions" className="cursor-pointer">Chọn tất cả</Label>
                            <Checkbox
                                id="select-all-permissions"
                                className='ml-2'
                                checked={permissions.length === Object.values(PERMISSIONS).reduce((acc, group) => acc + Object.values(group.permissions).length, 0)}
                                onCheckedChange={(checked) => {
                                    if (checked) {
                                        const allPermissions = Object.values(PERMISSIONS).flatMap((group) => Object.values(group.permissions).map((perm) => perm.key));
                                        setPermissions(allPermissions);
                                        setValue("permissions", allPermissions);
                                    } else {
                                        setPermissions([]);
                                        setValue("permissions", []);
                                    }
                                }}
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {
                                Object.entries(PERMISSIONS).map(([groupKey, groupValue]) => (
                                    <div key={groupKey} className="border p-2 rounded">
                                        <div className="font-medium mb-2">{groupValue.label}</div>
                                        <div className='grid grid-cols-1'>
                                            {Object.values(groupValue.permissions).map((permission) => (
                                                <div key={permission.key} className='flex items-start space-x-3'>
                                                    <Checkbox
                                                        id={permission.key}
                                                        checked={permissions.includes(permission.key)}
                                                        onCheckedChange={(checked) => handlePermissionChange(permission.key, checked as boolean)}
                                                        className='mt-1'
                                                    />
                                                    <div className='flex-1'>
                                                        <Label htmlFor={permission.key} className='text-sm cursor-pointer'>
                                                            {permission.label}
                                                        </Label>
                                                        <p className='text-xs text-muted-foreground mt-1'>{permission.description}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>

                <div className='flex justify-end gap-2 pt-4'>
                    <Button type='button' variant='destructive' onClick={() => onClose()}>
                        Hủy
                    </Button>
                    <Button disabled={!isValid} variant={"add"} type='submit'>
                        Lưu
                    </Button>
                </div>
            </form>
        </DialogContent>
    </Dialog>

});